import type { LabObservation, LabReferenceInterval } from "@/types/labs";
import { numericMeasurement } from "./labBiomarkers";

// Verified 2026-09-14. These are published comparisons, never a lab flag or
// personalized treatment target. Evidence and exclusions: docs/labs/reference-ranges.md.
const mayo = "https://www.mayocliniclabs.com/test-catalog/Overview/";
const aliases: Record<string, string[]> = {
  cholesterol: ["cholesterol", "total cholesterol", "cholesterol total"],
  hdl: ["hdl", "hdl cholesterol", "high density lipoprotein hdl"],
  ldl: ["ldl", "ldl cholesterol", "low density lipoprotein ldl"],
  triglycerides: ["triglycerides"],
  vldl: ["vldl", "vldl cholesterol", "verylow density lipo vldl"],
  apob: ["apolipoprotein b", "apolipoprotein b crl", "apob"],
  lpa: ["lipoprotein a", "lipoprotein a crl"],
  a1c: ["hemoglobin a1c", "hba1c", "a1c"],
  hscrp: ["hs crp", "hscrp", "c reactive protein high sensitivity"],
  creatinine: ["creatinine", "creatinine serum"],
  alp: ["alkaline phosphatase"],
  testosterone: ["total testosterone", "testosterone total"],
};
const nameKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

function validBand(band: LabReferenceInterval, unit: string) {
  return (
    band.unit === unit &&
    (band.low !== null || band.high !== null) &&
    [band.low, band.high].every(
      (value) => value === null || Number.isFinite(value),
    ) &&
    (band.low === null || band.high === null || band.low < band.high)
  );
}

export function labReferenceRange(
  item: LabObservation,
): LabReferenceInterval | null {
  const numeric = numericMeasurement(item);
  if (!numeric) return null;
  if (item.reference_interval)
    return validBand(item.reference_interval, numeric.unit)
      ? item.reference_interval
      : null;
  // Ambiguous, incompatible or textual lab ranges still outrank a generic fallback.
  if (item.has_source_range || item.reference_ranges?.length) return null;
  const age = item.reference_context?.age_at_collection;
  const sex = item.reference_context?.assigned_sex;
  if (age == null || !Number.isInteger(age) || age < 18 || age > 120)
    return null;
  const marker = Object.entries(aliases).find(([, names]) =>
    names.includes(nameKey(item.name)),
  )?.[0];
  const unit = numeric.unit;
  const build = (
    low: number | null,
    high: number | null,
    url: string,
    label: string,
    population = "Adults 18+",
    note = "A general comparison from another source. The reporting laboratory and your care team may use different ranges or targets.",
    highInclusive = true,
  ): LabReferenceInterval => ({
    low,
    high,
    low_inclusive: true,
    high_inclusive: highInclusive,
    unit,
    source: "published",
    origin: "published",
    label,
    source_url: url,
    source_title: url.includes("niddk")
      ? "NIH · NIDDK"
      : url.includes("mayocliniclabs")
        ? "Mayo Clinic Laboratories"
        : "Mayo Clinic",
    population,
    note,
  });
  const threshold = (
    high: number,
    path: string,
    label = "Published desirable threshold",
  ) => build(null, high, mayo + path, label, undefined, undefined, false);
  if (unit === "mg/dL") {
    if (marker === "cholesterol") return threshold(200, "616696");
    if (marker === "ldl") return threshold(100, "616696");
    if (marker === "triglycerides")
      return threshold(150, "616696", "Published triglyceride threshold");
    if (marker === "apob") return threshold(90, "614544");
    if (marker === "hdl" && sex)
      return build(
        sex === "male" ? 40 : 50,
        null,
        mayo + "616696",
        "Published HDL threshold",
        `${sex === "male" ? "Male" : "Female"} reference · adults 18+`,
      );
    if (marker === "vldl")
      return build(
        null,
        30,
        "https://www.mayoclinic.org/diseases-conditions/high-blood-cholesterol/expert-answers/vldl-cholesterol/faq-20058275",
        "Published VLDL threshold",
      );
    if (marker === "hscrp")
      return threshold(0.2, "606909", "Published hs-CRP threshold"); // 2 mg/L = 0.2 mg/dL, not standard CRP.
    if (marker === "creatinine" && sex)
      return build(
        sex === "male" ? 0.74 : 0.59,
        sex === "male" ? 1.35 : 1.04,
        mayo + "48216",
        "Published reference interval",
        `${sex === "male" ? "Male" : "Female"} reference · adults 18+`,
        "Serum creatinine comparison. Method, muscle mass, pregnancy and hormone therapy can change applicability.",
      );
  }
  if (marker === "lpa" && unit === "nmol/L")
    return threshold(75, "615007", "Published Lp(a) threshold");
  // Lp(a) mass and molar concentrations are NOT converted using a fixed factor.
  if (marker === "hscrp" && unit === "mg/L")
    return threshold(2, "606909", "Published hs-CRP threshold");
  if (marker === "a1c" && unit === "%")
    return build(
      null,
      5.7,
      "https://www.niddk.nih.gov/health-information/diagnostic-tests/a1c-test",
      "General screening threshold",
      "Adults · not a pregnancy or diabetes-management target",
      "This screening threshold does not diagnose diabetes. Pregnancy, anemia and other conditions can affect interpretation.",
      false,
    );
  if (marker === "alp" && unit === "U/L" && sex) {
    const low = sex === "female" ? 35 : age === 18 ? 55 : 40;
    const high = sex === "female" ? 104 : age === 18 ? 149 : 129;
    return build(
      low,
      high,
      mayo + "622157",
      "Published reference interval",
      `${sex === "male" ? "Male" : "Female"} reference · ${sex === "male" && age === 18 ? "age 18" : sex === "male" ? "ages 19+" : "adults 18+"}`,
      "Serum total alkaline phosphatase comparison. Pregnancy, growth and laboratory method can change the appropriate range.",
    );
  }
  // Mayo explicitly says this LC-MS/MS interval is not interchangeable with immunoassays.
  if (
    marker === "testosterone" &&
    unit === "ng/dL" &&
    sex &&
    ["lc ms ms", "liquid chromatography tandem mass spectrometry"].includes(
      nameKey(item.method || ""),
    )
  ) {
    const bounds =
      sex === "male"
        ? age === 18
          ? [300, 1200]
          : [240, 950]
        : age === 18
          ? [20, 75]
          : [8, 60];
    return build(
      bounds[0],
      bounds[1],
      mayo + "8533",
      "Published reference interval",
      `${sex === "male" ? "Male" : "Female"} reference · ${age === 18 ? "age 18" : "ages 19+"}`,
      "Morning serum samples measured by LC-MS/MS. This is not a hormone-therapy target and is not interchangeable with immunoassay ranges.",
    );
  }
  return null;
}

export function rangeText(band: LabReferenceInterval) {
  const numbers =
    band.low !== null && band.high !== null
      ? `${band.low}–${band.high}`
      : band.low !== null
        ? `${band.low_inclusive ? "≥" : ">"}${band.low}`
        : `${band.high_inclusive ? "≤" : "<"}${band.high}`;
  return `${numbers}${band.unit ? ` ${band.unit}` : ""}`;
}
